<!-- SPDX-License-Identifier: AGPL-3.0-only -->

<script setup lang="ts">
import { computed, onMounted, ref, watch, type ComputedRef, type Ref } from "vue";
import { useRouter, type Router } from "vue-router";
import { ApiRequestError } from "../api/http.js";
import type { TenantMember, TenantMembershipStatus } from "../api/tenant-members.js";
import { createInvitation, listInvitations, revokeInvitation, type TenantInvitation } from "../api/tenant-invitations.js";
import type { TenantRole } from "../api/tenants.js";
import { useAuthenticationStore } from "../stores/authentication.js";
import { useTenantMemberStore } from "../stores/tenant-members.js";
import { useTenantStore } from "../stores/tenants.js";
import UserActionIcon from "./UserActionIcon.vue";
import { confirmDialog } from "../dialogs/app-dialog.js";
import { formatDateTime } from "../formatting/date-time.js";
import { useUserSettingsStore } from "../stores/user-settings.js";

interface TenantMembersPanelProperties {
  readonly tenantId: string;
  readonly maxUsers: number;
}

const properties: TenantMembersPanelProperties = defineProps<TenantMembersPanelProperties>();
const authenticationStore: ReturnType<typeof useAuthenticationStore> = useAuthenticationStore();
const memberStore: ReturnType<typeof useTenantMemberStore> = useTenantMemberStore();
const tenantStore: ReturnType<typeof useTenantStore> = useTenantStore();
const userSettingsStore: ReturnType<typeof useUserSettingsStore> = useUserSettingsStore();
const router: Router = useRouter();
const email: Ref<string> = ref("");
const role: Ref<TenantRole> = ref("viewer");
const busyUserId: Ref<string | null> = ref(null);
const adding: Ref<boolean> = ref(false);
const invitations: Ref<readonly TenantInvitation[]> = ref([]);
const errorMessage: Ref<string | null> = ref(null);
const activeMemberCount: ComputedRef<number> = computed(
  (): number =>
    memberStore.members.filter(
      (member: TenantMember): boolean =>
        member.membershipStatus === "active" && member.userStatus === "active",
    ).length,
);

const roles: readonly { readonly value: TenantRole; readonly label: string }[] = [
  { value: "tenant_admin", label: "Tenant Admin" },
  { value: "probe_admin", label: "Probe Admin" },
  { value: "operator", label: "Operator" },
  { value: "viewer", label: "Viewer" },
];

function errorText(error: unknown): string {
  return error instanceof ApiRequestError ? error.message : "The member operation failed.";
}

function formatInvitationExpiry(value: string): string {
  return formatDateTime(value, userSettingsStore.settings.dateTimeFormat);
}

async function loadMembers(): Promise<void> {
  errorMessage.value = null;
  memberStore.reset();
  try {
    const results: [void, readonly TenantInvitation[]] = await Promise.all([
      memberStore.load(properties.tenantId), listInvitations(properties.tenantId),
    ]);
    invitations.value = results[1];
  } catch (error: unknown) {
    errorMessage.value = errorText(error);
  }
}

async function addMember(): Promise<void> {
  if (adding.value) {
    return;
  }
  adding.value = true;
  errorMessage.value = null;
  try {
    const created: TenantInvitation = await createInvitation(properties.tenantId, email.value, role.value);
    invitations.value = [created, ...invitations.value];
    email.value = "";
    role.value = "viewer";
  } catch (error: unknown) {
    errorMessage.value = errorText(error);
  } finally {
    adding.value = false;
  }
}

async function cancelInvitation(invitation: TenantInvitation): Promise<void> {
  const confirmed: boolean = await confirmDialog({ title: "Revoke tenant invitation?", message: `The pending invitation for ${invitation.email} will no longer be usable.`, confirmLabel: "Revoke invitation", tone: "danger" });
  if (!confirmed) return;
  busyUserId.value = invitation.id; errorMessage.value = null;
  try { await revokeInvitation(properties.tenantId, invitation.id); invitations.value = invitations.value.filter((item: TenantInvitation): boolean => item.id !== invitation.id); }
  catch (error: unknown) { errorMessage.value = errorText(error); }
  finally { busyUserId.value = null; }
}

async function refreshOwnTenantAccess(): Promise<void> {
  const userId: string | undefined = authenticationStore.session?.user.id;
  if (userId === undefined) {
    return;
  }
  tenantStore.reset();
  await tenantStore.initialize(userId);
  await router.replace({ name: "home" });
}

async function changeRole(userId: string, event: Event): Promise<void> {
  const target: HTMLSelectElement = event.target as HTMLSelectElement;
  const nextRole: TenantRole = target.value as TenantRole;
  busyUserId.value = userId;
  errorMessage.value = null;
  try {
    await memberStore.changeRole(userId, nextRole);
    if (userId === authenticationStore.session?.user.id) {
      await refreshOwnTenantAccess();
    }
  } catch (error: unknown) {
    errorMessage.value = errorText(error);
    target.value =
      memberStore.members.find((member: TenantMember): boolean => member.userId === userId)?.role ??
      "viewer";
  } finally {
    busyUserId.value = null;
  }
}

async function removeMember(userId: string, displayName: string): Promise<void> {
  const confirmed: boolean = await confirmDialog({ title: "Remove tenant member?", message: `${displayName} will permanently lose this tenant membership.`, confirmLabel: "Remove member", tone: "danger" });
  if (!confirmed) return;
  busyUserId.value = userId;
  errorMessage.value = null;
  try {
    await memberStore.remove(userId);
    if (userId === authenticationStore.session?.user.id) {
      await refreshOwnTenantAccess();
    }
  } catch (error: unknown) {
    errorMessage.value = errorText(error);
  } finally {
    busyUserId.value = null;
  }
}

async function toggleMemberStatus(member: TenantMember): Promise<void> {
  const nextStatus: TenantMembershipStatus = member.membershipStatus === "active"
    ? "disabled"
    : "active";
  const action: string = nextStatus === "active" ? "Enable" : "Disable";
  const confirmed: boolean = await confirmDialog({ title: `${action} tenant access?`, message: nextStatus === "disabled" ? `${member.displayName} will lose access to this tenant while retaining the assigned role.` : `${member.displayName} will regain access with the preserved ${member.role.replace("_", " ")} role.`, confirmLabel: `${action} access`, tone: nextStatus === "disabled" ? "danger" : "default" });
  if (!confirmed) return;
  busyUserId.value = member.userId;
  errorMessage.value = null;
  try {
    await memberStore.changeStatus(member.userId, nextStatus);
    if (member.userId === authenticationStore.session?.user.id) await refreshOwnTenantAccess();
  } catch (error: unknown) {
    errorMessage.value = errorText(error);
  } finally {
    busyUserId.value = null;
  }
}

onMounted(loadMembers);
watch((): string => properties.tenantId, loadMembers);
</script>

<template>
  <section class="content-panel member-panel">
    <div class="content-panel__heading">
      <div>
        <p class="eyebrow">Tenant administration</p>
        <h2>Members</h2>
      </div>
      <span class="coming-soon">
        {{ activeMemberCount }} / {{ maxUsers }} active users
      </span>
    </div>

    <form class="member-add-form" @submit.prevent="addMember">
      <label>
        <span>User email</span>
        <input v-model="email" type="email" required maxlength="320" placeholder="user@example.com" />
      </label>
      <label>
        <span>Initial role</span>
        <select v-model="role">
          <option v-for="availableRole in roles" :key="availableRole.value" :value="availableRole.value">
            {{ availableRole.label }}
          </option>
        </select>
      </label>
      <button class="button button--primary" type="submit" :disabled="adding">
        {{ adding ? "Sending…" : "Send invitation" }}
      </button>
    </form>

    <div v-if="invitations.length > 0" class="member-list">
      <h3>Pending invitations</h3>
      <article v-for="invitation in invitations" :key="invitation.id" class="member-row">
        <div class="tenant-row__mark">✉</div>
        <div class="tenant-row__identity"><strong>{{ invitation.email }}</strong><span>{{ invitation.role.replace("_", " ") }} · expires {{ formatInvitationExpiry(invitation.expiresAt) }}</span></div>
        <span>{{ invitation.deliveryStatus === "sent" ? "Email sent" : invitation.deliveryStatus }}</span>
        <button class="icon-button icon-button--danger" type="button" :disabled="busyUserId === invitation.id" title="Revoke invitation" :aria-label="`Revoke invitation for ${invitation.email}`" @click="cancelInvitation(invitation)"><UserActionIcon name="delete" /></button>
      </article>
    </div>

    <p v-if="errorMessage" class="form-error member-panel__error" role="alert">{{ errorMessage }}</p>
    <p v-if="memberStore.status === 'loading'" class="empty-state">Loading members…</p>
    <p v-else-if="memberStore.status === 'failed' && !errorMessage" class="empty-state">
      Members could not be loaded.
    </p>
    <div v-else class="member-list">
      <article
        v-for="member in memberStore.members"
        :key="member.userId"
        class="member-row"
        :class="{
          'member-row--membership-disabled': member.membershipStatus === 'disabled',
          'member-row--globally-disabled': member.userStatus !== 'active',
        }"
      >
        <div class="tenant-row__mark">{{ member.displayName.slice(0, 1).toUpperCase() }}</div>
        <div class="tenant-row__identity">
          <strong>{{ member.displayName }}</strong>
          <span>{{ member.email }}</span>
          <span v-if="member.membershipStatus === 'disabled'" class="member-status member-status--tenant">Tenant access disabled</span>
          <span v-if="member.userStatus !== 'active'" class="member-status member-status--global">Global account {{ member.userStatus }}</span>
        </div>
        <select
          :value="member.role"
          :disabled="busyUserId === member.userId"
          :aria-label="`Role for ${member.displayName}`"
          @change="changeRole(member.userId, $event)"
        >
          <option v-for="availableRole in roles" :key="availableRole.value" :value="availableRole.value">
            {{ availableRole.label }}
          </option>
        </select>
        <span class="row-actions">
          <button class="icon-button" type="button" :disabled="busyUserId === member.userId" :title="member.membershipStatus === 'active' ? 'Disable tenant access' : 'Enable tenant access'" :aria-label="`${member.membershipStatus === 'active' ? 'Disable' : 'Enable'} tenant access for ${member.displayName}`" @click="toggleMemberStatus(member)"><UserActionIcon name="status" /></button>
          <button class="icon-button icon-button--danger" type="button" :disabled="busyUserId === member.userId" title="Remove from tenant" :aria-label="`Remove ${member.displayName} from tenant`" @click="removeMember(member.userId, member.displayName)"><UserActionIcon name="delete" /></button>
        </span>
      </article>
    </div>
  </section>
</template>
